import { supabase } from "../supabase";
import { detectBrowserLanguage } from "../browserLanguage";

/**
 * Sign up with email + password.
 * Returns { user, error }.
 */
export async function signUp(email, password, fullName) {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: { data: { full_name: fullName, language: detectBrowserLanguage() } },
  });
  return { user: data?.user ?? null, error };
}

/**
 * Sign in with email + password.
 * Returns { user, error }.
 */
export async function signIn(email, password) {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  return { user: data?.user ?? null, error };
}

/** Sign out the current user. */
export async function signOut() {
  const { error } = await supabase.auth.signOut();
  return { error };
}

/**
 * Returns the currently logged-in user, or null.
 * Use this on app boot to restore session.
 */
export async function getSession() {
  const { data } = await supabase.auth.getSession();
  return data?.session?.user ?? null;
}

/**
 * Subscribe to auth state changes.
 * Calls callback(user | null) whenever the session changes.
 * Returns the unsubscribe function.
 */
export function onAuthChange(callback) {
  const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
    callback(session?.user ?? null);
  });
  return () => subscription.unsubscribe();
}

/** Fetch a user's public profile from the profiles table. */
export async function getProfile(userId) {
  const { data, error } = await supabase
    .from("profiles")
    .select("*")
    .eq("id", userId)
    .single();
  return { profile: data, error };
}

/** Update the current user's profile. */
export async function updateProfile(updates) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { error: new Error("Not authenticated") };
  const { data, error } = await supabase
    .from("profiles")
    .update(updates)
    .eq("id", user.id)
    .select()
    .single();
  return { profile: data, error };
}

/** Update the current Supabase auth user's metadata. */
export async function updateUserMetadata(metadata) {
  const { data, error } = await supabase.auth.updateUser({
    data: metadata,
  });
  return { user: data?.user ?? null, error };
}

/** Send a password reset email to the user. */
export async function sendPasswordReset(email) {
  const redirectTo = `${window.location.origin}/reset-password`;
  const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
  return { error };
}

/** Change the current user's password. */
export async function updatePassword(password) {
  const { data, error } = await supabase.auth.updateUser({ password });
  return { user: data?.user ?? null, error };
}

/** Permanently delete the current authenticated account. */
export async function deleteAccount() {
  const res = await supabase.functions.invoke("delete-account");
  if (res.error) return { error: res.error };
  // scope: "local" clears the local session without hitting /auth/v1/logout,
  // which would 403 since the user was already deleted server-side.
  const { error: signOutError } = await supabase.auth.signOut({ scope: "local" });
  return signOutError ? { error: signOutError } : { error: null };
}
